import { useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from "react";
import { FOCUSABLE_SELECTOR } from "../focusable";
import { switcherOptionClassName, type SwitcherSize } from "../styles/switcherStyles";

export interface TabOption<K extends string> {
  readonly key: K;
  readonly label: ReactNode;
  readonly disabled?: boolean;
}

interface TabsProps<K extends string> {
  /** Names the tab list for screen readers. */
  label: string;
  options: readonly TabOption<K>[];
  selected: K;
  onSelect: (key: K) => void;
  size?: SwitcherSize;
  /** Beside the tabs, before them: a title. */
  header?: ReactNode;
  /** Beside the tabs, after them: a hint or an action. */
  toolbar?: ReactNode;
  className?: string;
  /** The selected tab's panel. */
  children: ReactNode;
}

const MOVES: Readonly<Record<string, (index: number, count: number) => number>> = {
  ArrowRight: (index, count) => (index + 1) % count,
  ArrowLeft: (index, count) => (index - 1 + count) % count,
  Home: () => 0,
  End: (_index, count) => count - 1,
};

/**
 * Views of one thing, one shown at a time (WAI-ARIA tabs): the tab list names its panel,
 * the arrow keys, Home and End move between tabs and show them, and Tab moves into the
 * panel: to its first control, or to the panel itself when it has none (text only).
 * For choosing a value or filtering a list use SegmentedControl.
 */
export default function Tabs<K extends string>({ label, options, selected, onSelect, size = "small", header, toolbar, className, children }: TabsProps<K>) {
  const baseId = useId();
  const tabs = useRef(new Map<K, HTMLButtonElement>());
  const tabId = (key: K) => `${baseId}-tab-${key}`;
  const panelId = `${baseId}-panel`;
  const enabled = options.filter((option) => option.disabled !== true);
  const panel = useRef<HTMLDivElement>(null);
  const panelTakesFocus = useHasNothingToFocus(panel);
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const move = MOVES[event.key];
    const current = enabled.findIndex((option) => option.key === selected);
    if (move === undefined || enabled.length === 0) {
      return;
    }
    event.preventDefault();
    const next = enabled[move(Math.max(current, 0), enabled.length)];
    if (next !== undefined) {
      onSelect(next.key);
      tabs.current.get(next.key)?.focus();
    }
  };
  return (
    <div className={className}>
      <div className="flex flex-wrap items-center gap-2">
        {header}
        <div className="flex flex-wrap gap-1" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
          {options.map((option) => (
            <button
              key={option.key}
              ref={(element) => {
                if (element === null) tabs.current.delete(option.key);
                else tabs.current.set(option.key, element);
              }}
              type="button"
              role="tab"
              id={tabId(option.key)}
              aria-controls={panelId}
              aria-selected={option.key === selected}
              tabIndex={option.key === selected ? 0 : -1}
              disabled={option.disabled}
              className={switcherOptionClassName(size, option.key === selected)}
              onClick={() => {
                onSelect(option.key);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
        {toolbar}
      </div>
      <div ref={panel} role="tabpanel" id={panelId} aria-labelledby={tabId(selected)} tabIndex={panelTakesFocus ? 0 : undefined} className="mt-2">
        {children}
      </div>
    </div>
  );
}

/** Whether `element` holds nothing that takes focus, followed as its content changes (a panel loads later). */
function useHasNothingToFocus(element: RefObject<HTMLElement | null>): boolean {
  const [nothing, setNothing] = useState(false);
  useLayoutEffect(() => {
    const current = element.current;
    if (current === null) return;
    const check = () => { setNothing(current.querySelector(FOCUSABLE_SELECTOR) === null); };
    check();
    const observer = new MutationObserver(check);
    observer.observe(current, { childList: true, subtree: true, attributes: true, attributeFilter: ["disabled", "href", "tabindex", "contenteditable"] });
    return () => { observer.disconnect(); };
  }, [element]);
  return nothing;
}
